import React, { useState, useEffect, useMemo, useRef, forwardRef } from "react";
import Form from "@rjsf/core";
import type { FormProps } from "@rjsf/core";
import { RJSFSchema, UiSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { Button, View, Text, Loader } from "@components";
import classNames from "classnames";

import {
  CheckboxWidget,
  CheckboxesWidget,
  RadioWidget,
  SelectWidget,
  ToggleWidget,
  RadioHierarchyTreeWidget,
  RadioHierarchyTreeModalWidget,
  SecondsAsMinutesInput,
  SecondsAsHoursInput,
  SecondsAsDaysInput,
  SecondsAsWeeksInput,
  SchemaFormDatePicker,
  NumberInputWidget,
  TextareaWidget,
  TextWidget,
  PasswordWidget,
  CheckboxHierarchyTreeWidget,
  DescriptionWidget,
  HiddenWidget,
  SwitchButtonWidget,
} from "./widgets";
import {
  ErrorTemplate,
  HiddenErrorTemplate,
  FieldTemplate,
  ObjectTemplate,
  ObjectFieldTemplate,
  ArrayFieldTemplate,
} from "./templates";
import { MultiSelectField } from "./fields";
import { transformInput, transformOutput } from "./utils";
import "./form.style.scss";
import { syntaxHighlight } from "@utils/object";

type Props = {
  // https://stackoverflow.com/a/44771268/220839
  liveValidate?: boolean,
  showErrorList?: boolean,
  hideLiveErrors?: boolean,
  disabled?: boolean,
  schema: RJSFSchema,
  uiSchema?: UiSchema,
  columns?: number,
  externalSubmit: boolean,
  boxGrid?: boolean,
  customSubmitButton?: React$Node,
  onChange?: Function,
  onError?: Function,
  ...FormProps,
  ...PropsFromLocalize,
};

type State = $Exact<{
  key: number,
  formData: Object,
}>;

const SchemaForm = forwardRef(function(props, ref) {
  const { readonly, columns, boxGrid } = props;

  const _schema = props.schema;
  const _uiSchema = props.uiSchema;

  const _formData =
    props.dontTransformTheData === true
      ? props.formData
      : transformInput(props.formData || {}, props.schema);

  const [errors, setErrors] = useState([]);

  const [formData, setFormData] = useState(_formData);
  const [schema, setSchema] = useState(_schema);
  const [uiSchema, setUiSchema] = useState(_uiSchema);

  useEffect(() => {
    // Refreshing the schema when updated
    setSchema(_schema || {});
  }, [_schema]);
  useEffect(() => {
    // Refreshing the schema when updated
    if (JSON.stringify(_formData) !== JSON.stringify(formData)) {
      setFormData(_formData);
    }
  }, [_formData]);

  const schemaFormName = props.schema.urn || props.schema["$id"] || "SchemaForm";

  const formRef = useRef(ref);
  const errorsMap = {
    pattern: "INCORRECT_PATTERN",
    required: "REQUIRED_FIELD",
  };

  const modifiedUiSchema = useMemo(() => {
    const newUiSchema = { ...props.uiSchema };

    if (columns || boxGrid) {
      const options = { ...newUiSchema["ui:options"] };

      if (columns && !boxGrid) options.columns = columns;
      if (boxGrid) options.boxGrid = true;

      newUiSchema["ui:options"] = options;
    }

    return newUiSchema;
  }, [props.uiSchema, columns, boxGrid]);

  function handleSubmit(data: Object) {
    if (props.onSubmit && ref.current.state.errors.length < 1) {
      props.onSubmit(transformOutput(data.formData)); // WARNING !!!
      // Refreshing the schema and the validator upon changing the data
      setFormData(data.formData);
    }
  }

  function handleChange(data: Object, errorSchema: Object) {
    if (props.onChange) {
      props.onChange(transformOutput(data.formData), errorSchema); // WARNING !!!
      // Refreshing the schema and the validator upon changing the data
      setFormData(data.formData);
    }
  }

  function transformErrors(errors: Object[], schema: RJSFSchema, context: any): Object[] {
    const err = errors.map(error => `${error.property}: ${error.message}`);
    props.onError && props.onError(err);
    setErrors(err);
    return errors;
  }

  function onError(errors) {
    //Instead SchemaForm's console.error we just put small console.log message
    // console.log("onError", errors);
  }

  return (
    <View flex={1}>
      {!schema || !Object.entries(schema).length ? (
        <Loader />
      ) : (
        <Form
          title={schemaFormName}
          $id={schemaFormName}
          ref={ref}
          name={schemaFormName}
          uiSchema={modifiedUiSchema}
          schema={schema}
          formData={formData}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onError={onError} // Suppress the errors if u need
          widgets={{
            checkbox: CheckboxWidget,
            checkboxes: CheckboxesWidget,
            radio: RadioWidget,
            select: SelectWidget,
            textarea: TextareaWidget,
            password: PasswordWidget,
            text: TextWidget,
            string: TextWidget,
            toggle: ToggleWidget,
            description: DescriptionWidget,
            "date-picker": SchemaFormDatePicker,
            "radio-hierarchy-tree": RadioHierarchyTreeWidget,
            "radio-hierarchy-tree-modal": RadioHierarchyTreeModalWidget,
            "seconds-as-minutes-input": SecondsAsMinutesInput,
            "seconds-as-hours-input": SecondsAsHoursInput,
            "seconds-as-days-input": SecondsAsDaysInput,
            "seconds-as-weeks-input": SecondsAsWeeksInput,
            "number-input": NumberInputWidget,
            "checkbox-hierarchy-tree": CheckboxHierarchyTreeWidget,
            "switch-button": SwitchButtonWidget,
          }}
          fields={{ "multi-select": MultiSelectField }}
          templates={{
            FieldTemplate,
            ObjectTemplate,
            ObjectFieldTemplate,
            ArrayFieldTemplate,
            FieldErrorTemplate: props.hideLiveErrors === true ? HiddenErrorTemplate : ErrorTemplate,
          }}
          disabled={props.disabled}
          readonly={props.readonly}
          className={[
            "schema-form-wrapper",
            props.className,
            props.longLabels && "schema-form-long-labels",
            props.readonly && "schema-form-readonly",
            columns && `columns-${columns}`,
            boxGrid && "box-grid",
            props.hideLiveErrors && "hide-live-errors",
          ].join(" ")}
          transformErrors={transformErrors}
          noHtml5Validate={true}
          liveValidate={props.hasOwnProperty("liveValidate") ? props.liveValidate : false}
          showErrorList={props.hasOwnProperty("showErrorList") ? props.showErrorList : false}
          focusOnFirstError={true}
          validator={validator}
        >
          {props.children}
          {props.customSubmitButton ||
            (!props.externalSubmit && (
              <button
                className={classNames("button mb-2", {
                  "d-none": props.externalSubmit,
                  "d-flex align-items-center": !props.externalSubmit,
                })}
              >
                <span className="material-icons">replay_circle_filled</span>Submit
              </button>
            ))}
        </Form>
      )}
      {errors.length > 0 && (
        <View display="none">
          <View>Form name: </View>
          <View>{schemaFormName}</View>
          <View>Errors:</View>
          {errors &&
            errors.map(e => {
              return <View key={e}>{e}</View>;
            })}
        </View>
      )}
    </View>
  );
});
export default SchemaForm;
